import type { FC } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import { styled } from '@compiled/react';

import { Box, Text, xcss } from '@atlaskit/primitives';
import { N0 } from '@atlaskit/theme/colors';
import Heading from '@atlaskit/heading';
import CloseIcon from '@atlaskit/icon/core/close';
import Image from '@atlaskit/image';
import { ModalTransition } from '@atlaskit/modal-dialog';
import Button from '@atlaskit/button/standard-button';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { usePopupDwellTime } from '@atlassian/popup-dwell-time';

import { withIsSiteAdmin, type WithIsSiteAdminProps } from '@confluence/change-edition';
import { GradientCTAButton } from '@confluence/change-edition/entry-points/GradientCTAButton';
import { Attribution, TransparentErrorBoundary } from '@confluence/error-boundary';
import { useFeatureDiscovery } from '@confluence/feature-discovery';
import { OnboardingManualTrialModal } from '@confluence/premium-trial/entry-points/OnboardingManualTrialModal';
import { fg } from '@confluence/feature-gating';
import { useChoreographerAssets } from '@confluence/choreographer-services';

import { useShouldCheckEligibilityForSpotlight } from './useShouldCheckEligibilityForSpotlight';
import { setIsPremiumOnboardingHappened } from './isOnboardingHappenedUtils';
import leftIllustration from './images/Illustration.svg';
import automationIconLight from './images/automationIcon-light.svg';
import automationIconDark from './images/automationIcon-dark.svg';
import analyticsIconLight from './images/analyticsIcon-light.svg';
import analyticsIconDark from './images/analyticsIcon-dark.svg';
import teamCalendarsIconLight from './images/teamCalendarsIcon-light.svg';
import teamCalendarsIconDark from './images/teamCalendarsIcon-dark.svg';
import whiteboardIconLight from './images/whiteboardIcon-light.svg';
import whiteboardIconDark from './images/whiteboardIcon-dark.svg';
import advancedPermissionIconLight from './images/advancedPermissionIcon-light.svg';
import advancedPermissionIconDark from './images/advancedPermissionIcon-dark.svg';
import type { ListItemProps } from './ListItem';
import { ListItem } from './ListItem';
import { PremiumFeatureHighlightToggle } from './PremiumFeatureHighlightToggle';
import { usePremiumFeatureUsageIcon } from './usePremiumFeatureUsage';
import { PremiumFeatureKey } from './useTrackPremiumFeatureUsage';
import { PremiumTrialOnboardingSurvey } from './PremiumTrialOnboardingSurvey';
import { getIsTrialInLast15Days } from './getIsTrialInLast15Days';
import { usePTOModalVisibilityOnReverseTrial } from './usePTOModalVisibilityOnReverseTrial';
import { useIsPremiumOnboardingFeatureDiscoveryEnabled } from './useIsPremiumOnboardingFeatureDiscoveryEnabled';

export const i18n = defineMessages({
	modalTitle: {
		id: 'premium-onboarding.modal.title',
		description: 'Title for modal',
		defaultMessage: 'Welcome to your Premium trial',
	},
	modalBodyText: {
		id: 'premium-onboarding.modal.body.text',
		description: 'Body text for modal',
		defaultMessage: 'Explore Confluence’s most powerful features',
	},
	modalBodyTextPTO2: {
		id: 'premium-onboarding.modal.body.text.pto2',
		description: 'Body text for modal',
		defaultMessage: 'Get started by exploring our 5 most popular features.',
	},
	modalBodyTextPTO2WithoutAutomation: {
		id: 'premium-onboarding.modal.body.text.pto2.no.automation',
		description: 'Body text for modal',
		defaultMessage: 'Get started by exploring our 4 most popular features.',
	},
	modalTitleBeginningOfTrial: {
		id: 'premium-onboarding.modal-beginning-of-trial.title.pto2',
		description: 'Title for modal at beginning of premium trial',
		defaultMessage: 'Your Premium feature guide',
	},
	modalTitleHalfwayThroughTrial: {
		id: 'premium-onboarding.modal-halfway-through-trial.title.pto2',
		description: 'Title for modal when halfway through premium trial',
		defaultMessage:
			'You have {daysLeft, plural, one {# day} other {# days}} left on your Premium trial 🎉',
	},
	modalBodyTextBeginningOfTrial: {
		id: 'premium-onboarding.modal-beginning-of-trial.body.text.pto2',
		description: 'Body text for modal at beginning of premium trial',
		defaultMessage: 'Features will get a checkmark once you’ve used them.',
	},
	modalBodyTextHalfwayThroughTrialSentence1: {
		id: 'premium-onboarding.modal-halfway-through-trial.body.text.sentence1.pto2',
		description:
			'Body text for modal when halfway through premium trial describing current feature usage',
		defaultMessage: 'You’ve used <b>{count}</b> of Premium’s most popular features.',
	},
	modalBodyTextHalfwayThroughTrialSentence2: {
		id: 'premium-onboarding.modal-halfway-through-trial.body.text.sentence2.pto2',
		description:
			'Body text for modal when halfway through premium trial encouraging more feature usage',
		defaultMessage: 'Get the most out of your trial by exploring features you haven’t tried yet.',
	},
	modalBodyTextHalfwayThroughTrialSentence3: {
		id: 'premium-onboarding.modal-halfway-through-trial.body.text.sentence3.pto2',
		description:
			'Body subtext for modal when all of the most popular Premium features are used halfway through the premium trial',
		defaultMessage: 'You’ve used <b>all</b> of Premium’s most popular features.',
	},
	toggleTitlePTO2: {
		id: 'premium-onboarding.toggle.title.pto2',
		description: 'Title for toggle',
		defaultMessage: 'Highlight Premium features in Confluence',
	},
	toggleBodyText: {
		id: 'premium-onboarding.toggle.body.text',
		description: 'Body text for toggle',
		defaultMessage: 'We’ll help you identify Premium features with a label.',
	},
	buttonText: {
		id: 'premium-onboarding.button.text',
		description: 'CTA button text',
		defaultMessage: 'Explore features',
	},
	analyticsTitle: {
		id: 'premium-onboarding.analytics.title',
		description: 'Title for analytics feature',
		defaultMessage: 'Analytics',
	},
	analyticsBodyTextPTO2: {
		id: 'premium-onboarding.analytics.body.text.pto2',
		description: 'Body text for analytics feature',
		defaultMessage: 'See user engagement and views at the site, space, and page level.',
	},
	teamCalendarsTitle: {
		id: 'premium-onboarding.team-calendars.title',
		description: 'Title for team calendars feature',
		defaultMessage: 'Team calendars',
	},
	teamCalendarsBodyText: {
		id: 'premium-onboarding.team-calendars.body.text',
		description: 'Body text for team calendars feature',
		defaultMessage: 'Keep one single source of truth for your team’s schedules and due dates.',
	},
	unlimitedStorageAndWhiteboardsTitle: {
		id: 'premium-onboarding.unlimited-storage-and-whiteboards.title.pto2',
		description: 'Title for unlimited storage and whiteboards feature',
		defaultMessage: 'Unlimited storage and whiteboards',
	},
	whiteboardBodyText: {
		id: 'premium-onboarding.whiteboard.body.text',
		description: 'Body text for whiteboard feature',
		defaultMessage: 'Never run out of space to turn ideas into actions.',
	},
	automationTitle: {
		id: 'premium-onboarding.automation.title',
		description: 'Title for automation feature',
		defaultMessage: 'Automation',
	},
	automationBodyText: {
		id: 'premium-onboarding.automation.body.text',
		description: 'Body text for automation feature',
		defaultMessage:
			'Save time by creating rules to change a page status, create a Jira task, and more.',
	},
	advancedPermissionsTitle: {
		id: 'premium-onboarding.advanced.permissions.title',
		description: 'Title for advanced permissions feature',
		defaultMessage: 'Advanced permissions',
	},
	advancedPermissionsBodyTextPTO2: {
		id: 'premium-onboarding.advanced.permissions.body.text.pto2',
		description: 'Body text for advanced permissions feature',
		defaultMessage:
			'Review permission-related issues and make sure your team has proper access to content.',
	},
	additionalFeaturesTitle: {
		id: 'premium-onboarding.title.additional.features',
		description: 'Title text for additional features',
		defaultMessage: 'Plus:',
	},
});

const NUMBER_OF_PREMIUM_FEATURES = 4;

const SURVEY_KEY = 'premium.trial.features.usefulness.survey';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Layout = styled.span({
	display: 'flex',
	flexDirection: 'row',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ImageContainer = styled.div({
	width: 220,
	minInlineSize: 'max-content',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TextArea = styled.div({
	width: 500,
	backgroundColor: token('color.text.inverse', N0),
});

const textAreaBodyStyles = xcss({
	paddingRight: 'space.500',
	paddingBottom: 'space.300',
	paddingLeft: 'space.500',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SecondTitle = styled(Heading)({
	marginTop: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CloseButton = styled.div({
	paddingRight: token('space.050', '4px'),
	paddingTop: token('space.200', '16px'),
	textAlign: 'right',
	minInlineSize: 'max-content',
});

const listItemWrapperStyles = xcss({
	marginTop: 'space.300',
	marginBottom: 'space.300',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonWrapper = styled.div({
	width: '100%',
	boxSizing: 'box-content',
	textAlign: 'right',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ToggleWrapper = styled.div({
	width: '100%',
	boxSizing: 'box-content',
	display: 'flex',
	marginBottom: token('space.300', '24px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ToggleContent = styled.label({
	flexDirection: 'column',
});

const togglePaddingStyles = xcss({
	paddingTop: 'space.100',
	paddingRight: 'space.150',
});

export enum TriggerFrom {
	PILL = 'pill',
	ONBOARDING = 'onboarding',
	MID_TRIAL_ONBOARDING = 'midTrialOnboarding',
	HOME_ONBOARDING_TAKEOVER_V2 = 'homeOnboardingTakeoverV2',
}

type ModalComponentType = {
	onClose?: () => void;
	stop?: () => void;
	triggerFrom?: TriggerFrom;
	daysLeft?: number;
	source?: string;
} & WithIsSiteAdminProps;

const ModalComponent: FC<ModalComponentType> = ({
	isSiteAdmin,
	onClose,
	stop,
	triggerFrom,
	daysLeft = 0,
	source = 'premiumTrialOnboardingModal',
}) => {
	const [isOpen, setIsOpen] = useState(true);
	const { Modal } = useChoreographerAssets();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [{ isSpotlightCheckEnabled }, { enableSpotlightCheck }] =
		useShouldCheckEligibilityForSpotlight();

	const [showSurvey, stopShowingSurvey] = useFeatureDiscovery(SURVEY_KEY);

	const { isPTORTEnabled, loading: isPTORTEnabledLoading } =
		useIsPremiumOnboardingFeatureDiscoveryEnabled({
			shouldCheckIsInTrial: false,
		});

	const { showMidTrialModal: showPTORTMidTrialModal } = usePTOModalVisibilityOnReverseTrial({
		isPTORTEnabled,
		isSiteAdmin,
	});

	const modalRef = useRef<HTMLDivElement | null>(null);
	const { getDwellTime } = usePopupDwellTime(modalRef);

	const isTrialHalfWayThrough = isPTORTEnabled
		? showPTORTMidTrialModal
		: getIsTrialInLast15Days(daysLeft);

	const isTriggeredFromPillOrMidTrial = triggerFrom !== TriggerFrom.ONBOARDING;

	let modalBody = fg('a4c_free_and_standard_editions') ? (
		<FormattedMessage {...i18n.modalBodyTextPTO2WithoutAutomation} />
	) : (
		<FormattedMessage {...i18n.modalBodyTextPTO2} />
	);

	let modalTitle = i18n.modalTitle;

	if (isTriggeredFromPillOrMidTrial) {
		modalBody = <FormattedMessage {...i18n.modalBodyTextBeginningOfTrial} />;
		modalTitle = i18n.modalTitleBeginningOfTrial;
	}

	const sharedFeaturesMapper: Array<ListItemProps & { featureKey: PremiumFeatureKey }> = [
		{
			title: i18n.analyticsTitle,
			bodyText: i18n.analyticsBodyTextPTO2,
			img: analyticsIconLight,
			darkImg: analyticsIconDark,
			featureKey: PremiumFeatureKey.ANALYTICS,
		},
		{
			title: i18n.teamCalendarsTitle,
			bodyText: i18n.teamCalendarsBodyText,
			img: teamCalendarsIconLight,
			darkImg: teamCalendarsIconDark,
			featureKey: PremiumFeatureKey.TEAM_CALENDAR,
		},
		{
			title: i18n.unlimitedStorageAndWhiteboardsTitle,
			bodyText: i18n.whiteboardBodyText,
			img: whiteboardIconLight,
			darkImg: whiteboardIconDark,
			featureKey: PremiumFeatureKey.UNLIMITED_STORAGE,
		},
		{
			title: i18n.automationTitle,
			bodyText: i18n.automationBodyText,
			img: automationIconLight,
			darkImg: automationIconDark,
			featureKey: PremiumFeatureKey.AUTOMATION,
		},
		{
			title: i18n.advancedPermissionsTitle,
			bodyText: i18n.advancedPermissionsBodyTextPTO2,
			img: advancedPermissionIconLight,
			darkImg: advancedPermissionIconDark,
			featureKey: PremiumFeatureKey.ADVANCED_PERMISSIONS,
		},
	];

	if (fg('a4c_free_and_standard_editions')) {
		const automationIdx = sharedFeaturesMapper.findIndex(
			(feature) => feature.title === i18n.automationTitle,
		);
		sharedFeaturesMapper.splice(automationIdx, 1);
	}

	const { updatedSharedFeaturesMapperWithCheckIconStatus, totalFeaturesUsed, pto2Attributes } =
		usePremiumFeatureUsageIcon({
			shouldUpdate: isTriggeredFromPillOrMidTrial,
			rawSharedFeaturesMapper: sharedFeaturesMapper,
		});

	useEffect(() => {
		triggerFrom === TriggerFrom.ONBOARDING && setIsPremiumOnboardingHappened(true);
	}, [triggerFrom]);

	const areAllFeaturesUsed = totalFeaturesUsed === NUMBER_OF_PREMIUM_FEATURES;
	if (isTrialHalfWayThrough && isTriggeredFromPillOrMidTrial) {
		modalTitle = i18n.modalTitleHalfwayThroughTrial;
		modalBody = areAllFeaturesUsed ? (
			<FormattedMessage
				{...i18n.modalBodyTextHalfwayThroughTrialSentence3}
				values={{
					b: (chunks: React.ReactNode[]) => <b>{chunks}</b>,
				}}
			/>
		) : (
			<>
				<FormattedMessage
					{...i18n.modalBodyTextHalfwayThroughTrialSentence1}
					values={{
						count: totalFeaturesUsed,
						b: (chunks: React.ReactNode[]) => <b>{chunks}</b>,
					}}
				/>{' '}
				<FormattedMessage {...i18n.modalBodyTextHalfwayThroughTrialSentence2} />
			</>
		);
	}

	const closeModal = useCallback(
		async (actionSubjectId: any = '') => {
			actionSubjectId &&
				triggerFrom !== TriggerFrom.ONBOARDING &&
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId,
						source,
						attributes: {
							dwellTime: getDwellTime(),
							isPTORTEnabled,
							triggerFrom,
						},
					},
				}).fire();
			onClose?.();
			stop?.();
			setIsOpen(false);
			if (isSpotlightCheckEnabled === false && triggerFrom === TriggerFrom.ONBOARDING) {
				await enableSpotlightCheck();
			}
		},
		[
			createAnalyticsEvent,
			source,
			getDwellTime,
			isPTORTEnabled,
			triggerFrom,
			onClose,
			stop,
			isSpotlightCheckEnabled,
			enableSpotlightCheck,
		],
	);

	useEffect(() => {
		if (!isPTORTEnabledLoading && triggerFrom !== TriggerFrom.ONBOARDING) {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: source,
					attributes: {
						isSiteAdmin,
						triggeredFrom: triggerFrom === TriggerFrom.PILL ? 'featureGuide' : triggerFrom,
						daysLeft,
						numPremiumFeaturesUsed: totalFeaturesUsed,
						...pto2Attributes,
						isPTORTEnabled,
					},
				},
			}).fire();
		}
		//making sure that this event gets fired only once
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isPTORTEnabledLoading]);

	const handleErrorBoundary = () => {
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				action: 'unhandledPremiumTrialError',
				source,
				actionSubject: 'ui',
				attributes: {
					attribution: Attribution.EDITIONS,
				},
			},
		}).fire();
	};

	if (triggerFrom === TriggerFrom.ONBOARDING) {
		return <OnboardingManualTrialModal onClose={closeModal} stop={stop} />;
	}

	return (
		<TransparentErrorBoundary attribution={Attribution.EDITIONS} onError={handleErrorBoundary}>
			<ModalTransition>
				{isOpen && (
					// To ensure that the close button isn't in focus when the modal is opened, disable auto focus on the modal
					<Modal
						onClose={() => closeModal('premiumTrialOnboardingModalCloseButton')}
						autoFocus={false}
						width={720}
					>
						<Layout ref={modalRef}>
							<ImageContainer>
								{/* Need a smaller height here for the image to make it adjustable based on height of the modal */}
								<Image
									src={leftIllustration}
									srcDark={leftIllustration}
									css={{
										objectFit: 'cover',
										minHeight: '100%',
										width: '220px',
										height: '400px !important',
									}}
								/>
							</ImageContainer>
							<TextArea>
								<CloseButton>
									<Button
										appearance="link"
										onClick={() => closeModal('premiumTrialOnboardingModalCloseButton')}
									>
										<CloseIcon label="Close Modal" color={token('color.text')} />
									</Button>
								</CloseButton>
								<Box xcss={textAreaBodyStyles}>
									<Heading size="medium" as="h1">
										<FormattedMessage {...modalTitle} values={{ daysLeft }} />
									</Heading>
									<SecondTitle as="h2" size={isTrialHalfWayThrough ? 'small' : 'xxsmall'}>
										{modalBody}
									</SecondTitle>
									<Box xcss={listItemWrapperStyles}>
										{Object.keys(updatedSharedFeaturesMapperWithCheckIconStatus)
											.filter(
												(key) =>
													!(
														updatedSharedFeaturesMapperWithCheckIconStatus[key].featureKey ===
															PremiumFeatureKey.UNLIMITED_STORAGE && isTriggeredFromPillOrMidTrial
													),
											)
											.map((key) => (
												<ListItem
													key={key}
													bodyText={updatedSharedFeaturesMapperWithCheckIconStatus[key].bodyText}
													checkIconStatus={
														updatedSharedFeaturesMapperWithCheckIconStatus[key].checkIconStatus
													}
													darkImg={updatedSharedFeaturesMapperWithCheckIconStatus[key].darkImg}
													img={updatedSharedFeaturesMapperWithCheckIconStatus[key].img}
													title={updatedSharedFeaturesMapperWithCheckIconStatus[key].title}
												/>
											))}
									</Box>
									{/* We do not render toggle and CTA button in PTO Phase 2 when modal is triggered from Pill */}
									{isTriggeredFromPillOrMidTrial ? null : (
										<>
											<ToggleWrapper>
												<Box xcss={togglePaddingStyles}>
													<PremiumFeatureHighlightToggle
														id="prem-feature-highlight-toggle-modal"
														source="premTrialOnboardingModal"
														isPTORTEnabled={isPTORTEnabled}
													/>
												</Box>
												<ToggleContent htmlFor="prem-feature-highlight-toggle-modal">
													<Text as="p">
														<FormattedMessage {...i18n.toggleTitlePTO2} />
													</Text>
													<Text as="p" size="small">
														<FormattedMessage {...i18n.toggleBodyText} />
													</Text>
												</ToggleContent>
											</ToggleWrapper>

											<ButtonWrapper>
												<GradientCTAButton
													onClick={() => closeModal('premiumTrialOnboardingModalExploreFeatures')}
												>
													<FormattedMessage {...i18n.buttonText} />
												</GradientCTAButton>
											</ButtonWrapper>
										</>
									)}
									{isTriggeredFromPillOrMidTrial &&
										showSurvey &&
										isTrialHalfWayThrough &&
										totalFeaturesUsed > 0 &&
										!isPTORTEnabledLoading && (
											<PremiumTrialOnboardingSurvey
												trialDaysLeft={daysLeft}
												totalFeaturesUsed={totalFeaturesUsed}
												stopShowingSurvey={stopShowingSurvey}
												isSiteAdmin={isSiteAdmin}
												pto2Attributes={pto2Attributes}
												isPTORTEnabled={isPTORTEnabled}
											/>
										)}
								</Box>
							</TextArea>
						</Layout>
					</Modal>
				)}
			</ModalTransition>
		</TransparentErrorBoundary>
	);
};

export const PremiumTrialOnboardingModal = withIsSiteAdmin(ModalComponent);
